import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, Input, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { AppNotification, NotificationsService } from '../../services/notifications.service';
import { TranslationService } from '../../services/translation.service';
import { formatNotificationParams, relativeTime } from './notification-format';

@Component({
  selector: 'app-notification-bell',
  imports: [CommonModule, NzIconModule, TranslatePipe],
  templateUrl: './notification-bell.component.html',
  styleUrls: ['./notification-bell.component.scss'],
})
export class NotificationBellComponent {
  private readonly notifications = inject(NotificationsService);
  private readonly translation = inject(TranslationService);
  private readonly router = inject(Router);
  private readonly host = inject(ElementRef<HTMLElement>);

  @Input() isCollapsed = false;

  readonly open = signal(false);
  readonly unreadCount = this.notifications.unreadCount;
  readonly recent = this.notifications.recent;
  readonly loading = this.notifications.loading;

  toggle(event: Event): void {
    event.stopPropagation();
    const next = !this.open();
    this.open.set(next);
    if (next) void this.notifications.refresh();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.open() && !this.host.nativeElement.contains(event.target as Node)) {
      this.open.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.open.set(false);
  }

  params(notification: AppNotification): Record<string, string> {
    return formatNotificationParams(notification.params, this.currentLang);
  }

  timeAgo(notification: AppNotification): string {
    return relativeTime(notification.createdAt, this.currentLang);
  }

  async openNotification(notification: AppNotification): Promise<void> {
    this.open.set(false);
    if (!notification.readAt) {
      await this.notifications.markRead(notification._id);
    }
    if (notification.link) {
      void this.router.navigateByUrl(notification.link);
    }
  }

  async markAllRead(event: Event): Promise<void> {
    event.stopPropagation();
    await this.notifications.markAllRead();
  }

  viewAll(): void {
    this.open.set(false);
    void this.router.navigate(['/notifications']);
  }

  private get currentLang(): string {
    return this.translation.getCurrentLangValue();
  }
}
